import React, { useEffect, useMemo, useRef, useState } from "react"

import {
  KeyValueRowsEditor,
  type KeyValueEditorRow,
} from "@/components/forms/KeyValueRowsEditor"

interface MetadataInputProps {
  metadata: Record<string, string>
  onChange: (metadata: Record<string, string>) => void
  onValidityChange?: (isValid: boolean) => void
  className?: string
}

interface MetadataRow extends KeyValueEditorRow {
  id: number
}

let nextMetadataRowId = 0

const createRow = (key = "", value = ""): MetadataRow => ({
  id: nextMetadataRowId++,
  key,
  value,
})

const metadataToRows = (metadata: Record<string, string>): MetadataRow[] => {
  const rows = Object.entries(metadata).map(([key, value]) =>
    createRow(key, value),
  )
  return rows.length > 0 ? rows : [createRow()]
}

const rowsToMetadata = (rows: MetadataRow[]): Record<string, string> => {
  const metadata: Record<string, string> = {}
  for (const row of rows) {
    const key = row.key.trim()
    if (key) {
      metadata[key] = row.value
    }
  }
  return metadata
}

const recordsMatch = (
  left: Record<string, string>,
  right: Record<string, string>,
) => JSON.stringify(left) === JSON.stringify(right)

const getDuplicateKeys = (rows: MetadataRow[]): Set<string> => {
  const counts = new Map<string, number>()
  for (const row of rows) {
    const key = row.key.trim()
    if (key) {
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
  }
  return new Set(
    Array.from(counts.entries())
      .filter(([, count]) => count > 1)
      .map(([key]) => key),
  )
}

export function MetadataInput({
  metadata,
  onChange,
  onValidityChange,
  className = "",
}: MetadataInputProps) {
  const [rows, setRows] = useState<MetadataRow[]>(() =>
    metadataToRows(metadata),
  )
  const lastCommittedMetadataRef = useRef(metadata)
  const duplicateKeys = useMemo(() => getDuplicateKeys(rows), [rows])

  useEffect(() => {
    if (!recordsMatch(lastCommittedMetadataRef.current, metadata)) {
      setRows(metadataToRows(metadata))
      lastCommittedMetadataRef.current = metadata
    }
  }, [metadata])

  useEffect(() => {
    onValidityChange?.(duplicateKeys.size === 0)
  }, [duplicateKeys, onValidityChange])

  const commitRows = (nextRows: MetadataRow[]) => {
    setRows(nextRows)
    if (getDuplicateKeys(nextRows).size === 0) {
      const nextMetadata = rowsToMetadata(nextRows)
      lastCommittedMetadataRef.current = nextMetadata
      onChange(nextMetadata)
    }
  }

  return (
    <div className={className}>
      <KeyValueRowsEditor
        rows={rows}
        onChange={(id, patch) =>
          commitRows(
            rows.map((row) => (row.id === id ? { ...row, ...patch } : row)),
          )
        }
        onRemove={(id) => {
          const remaining = rows.filter((row) => row.id !== id)
          commitRows(remaining.length > 0 ? remaining : [createRow()])
        }}
        onAdd={() => commitRows([...rows, createRow()])}
        keyLabel="Metadata key"
        valueLabel="Metadata value"
        addLabel="Add metadata"
        getKeyError={(row) =>
          duplicateKeys.has(row.key.trim())
            ? "Metadata keys must be unique."
            : undefined
        }
      />
    </div>
  )
}
